(() => {
  const controllers = new Set();
  let userEdited = false;
  const rootSelector = '[data-native-browse]';
  const formSelector = '[data-native-filter-form], [data-native-sort-form], .native-search__query';

  class NativeBrowse {
    constructor(root) {
      this.root = root;
      this.requestId = 0;
      this.controller = null;
      this.openGroups = new Map();
      this.init();
    }

    init() {
      const root = this.root;
      root.dataset.nativeFiltersReady = 'true';
      root.classList.add('native-browse--enhanced');
      const toggle = root.querySelector('[data-native-filter-toggle]');
      const panel = root.querySelector('[data-native-filter-panel]');
      toggle?.classList.add('is-enhanced');
      panel?.classList.add('is-enhanced');
      toggle?.addEventListener('click', () => {
        const open = panel.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        if (open) this.fitPanel(true);
      });
      panel?.addEventListener('keydown', event => {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
          event.preventDefault();
          panel.classList.remove('is-open');
          toggle?.setAttribute('aria-expanded', 'false');
          toggle?.focus();
        }
      });
      root.querySelector('[data-native-sort-form]')?.classList.add('is-enhanced');
      root.addEventListener('submit', event => {
        const form = event.target.closest(formSelector);
        if (!form || event.defaultPrevented || !form.checkValidity()) return;
        event.preventDefault();
        this.navigate(this.formURL(form), { focus: this.focusKey(document.activeElement), closeFilters: form.matches('[data-native-filter-form]') && this.mobile() });
      });
      root.addEventListener('change', event => {
        const input = event.target;
        if (input.matches('[data-native-sort]')) input.form.requestSubmit();
        else if (!this.mobile() && input.closest('[data-native-filter-form]') && input.matches('input[type="checkbox"], input[type="number"]') && input.form.checkValidity()) input.form.requestSubmit();
      });
      root.addEventListener('click', event => {
        const link = event.target.closest('.native-browse__pagination a, .native-filters__active a, .native-filters__head a, .native-search__empty a');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
        const url = new URL(link.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;
        event.preventDefault();
        this.navigate(url, { focus: 'results', scroll: link.closest('.native-browse__pagination') !== null });
      });
      this.status = document.createElement('div');
      this.status.className = 'native-browse__status';
      this.status.setAttribute('data-native-browse-status', '');
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      (root.querySelector('[data-native-filter-toolbar]') || root.querySelector('.native-search__query') || root).after(this.status);
      this.root.querySelectorAll('.native-filters__group').forEach(group => {
        const name = group.dataset.filterName;
        if (name && this.openGroups.has(name) && !group.querySelector('.native-filters__selected')) group.open = this.openGroups.get(name);
      });
    }

    mobile() {
      return window.matchMedia ? window.matchMedia('(max-width: 900px)').matches : window.innerWidth <= 900;
    }

    fitPanel(reveal = false) {
      const panel = this.root.querySelector('[data-native-filter-panel]');
      if (!panel) return;
      if (!this.mobile() || !panel.classList.contains('is-open')) {
        panel.style.removeProperty('--native-filter-available-height');
        return;
      }
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      let top = panel.getBoundingClientRect().top;
      if (reveal && (top < 80 || top > viewportHeight - 220)) {
        this.root.querySelector('[data-native-filter-toolbar]')?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
        top = panel.getBoundingClientRect().top;
      }
      panel.style.setProperty('--native-filter-available-height', `${Math.max(120, viewportHeight - Math.max(0, top) - 16)}px`);
    }

    formURL(form) {
      const url = new URL(form.action, location.origin);
      const filterForm = this.root.querySelector('[data-native-filter-form]');
      const source = form.matches('[data-native-sort-form]') && filterForm ? filterForm : form;
      url.search = new URLSearchParams(new FormData(source)).toString();
      if (!form.matches('.native-search__query')) {
        const sort = this.root.querySelector('[data-native-sort]');
        if (sort) url.searchParams.set(sort.name, sort.value);
      }
      [...url.searchParams.entries()].forEach(([key, value]) => { if (value === '') url.searchParams.delete(key); });
      const current = new URL(location.href);
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      return url;
    }

    focusKey(element) {
      if (!element || !this.root.contains(element)) return null;
      if (element.matches('[data-native-sort]')) return { sort: true };
      if (element.matches('.native-filters__apply')) return { apply: true };
      if (element.name) return { name: element.name, value: element.value, type: element.type };
      return null;
    }

    restoreFocus(key, closeFilters) {
      let target;
      if (closeFilters) target = this.root.querySelector('[data-native-filter-toggle]');
      else if (key?.sort) target = this.root.querySelector('[data-native-sort]');
      else if (key?.apply) target = this.root.querySelector('.native-filters__apply');
      else if (key?.name) target = [...this.root.querySelectorAll('input, select')].find(input => input.name === key.name && input.type === key.type && (input.type !== 'checkbox' || input.value === key.value));
      if (!target && key) target = this.root.querySelector('[data-native-filter-toolbar]') || this.root.querySelector('h1');
      if (target && target.getClientRects().length) {
        if (!target.matches('input, select, button, a')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    async navigate(url, options = {}) {
      url = new URL(url);
      const context = new URL(location.href);
      if (options.history !== false) ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
        if (context.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, context.searchParams.get(key));
      });
      const id = ++this.requestId;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.root.querySelectorAll('.native-filters__group').forEach(group => {
        if (group.dataset.filterName) this.openGroups.set(group.dataset.filterName, group.open);
      });
      const panelOpen = this.root.querySelector('[data-native-filter-panel]')?.classList.contains('is-open');
      const status = this.status;
      status.replaceChildren(document.createTextNode(this.message('loading')));
      status.classList.remove('is-error', 'is-complete');
      this.root.setAttribute('aria-busy', 'true');
      const requestURL = new URL(url);
      requestURL.searchParams.set('section_id', this.root.dataset.sectionId);
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(requestURL.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        const html = await response.text();
        if (id !== this.requestId || controller.signal.aborted) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = [...parsed.querySelectorAll(rootSelector)].find(node => node.dataset.sectionId === this.root.dataset.sectionId);
        if (!replacement) throw new Error('Section content missing');
        document.dispatchEvent(new CustomEvent('native:browse:before-update', { detail: { root: this.root, url } }));
        this.status.remove();
        this.root.replaceWith(replacement);
        this.root = replacement;
        this.init();
        if (panelOpen && !options.closeFilters) {
          this.root.querySelector('[data-native-filter-panel]')?.classList.add('is-open');
          this.root.querySelector('[data-native-filter-toggle]')?.setAttribute('aria-expanded', 'true');
          this.fitPanel();
        }
        if (options.history !== false && url.href !== location.href) history.pushState({ ...history.state, nativeBrowse: true }, '', url.href);
        this.root.removeAttribute('aria-busy');
        const count = this.root.querySelector('.native-browse-toolbar__count')?.textContent.trim() || '';
        this.status.textContent = count;
        this.status.classList.add('is-complete');
        document.dispatchEvent(new CustomEvent('native:browse:updated', { detail: { root: this.root, url } }));
        this.restoreFocus(options.focus, options.closeFilters);
        if (options.scroll || options.closeFilters) (this.root.querySelector('[data-native-filter-toolbar]') || this.root).scrollIntoView({ block: 'start', behavior: 'instant' });
      } catch (error) {
        if (id !== this.requestId) return;
        this.root.removeAttribute('aria-busy');
        status.classList.add('is-error');
        status.replaceChildren(document.createTextNode(this.message('loadError') + ' '));
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = this.message('retry');
        retry.addEventListener('click', () => this.navigate(url, options));
        const fallback = document.createElement('a');
        fallback.href = url.href;
        fallback.textContent = this.message('openFullPage');
        status.append(retry, document.createTextNode(' '), fallback);
      } finally {
        clearTimeout(timeout);
        if (id === this.requestId) this.controller = null;
      }
    }

    message(key) {
      return this.root.querySelector('[data-native-filter-toolbar]')?.dataset[key] || this.root.dataset[key] || '';
    }
  }

  function init(container = document) {
    const roots = container.matches?.(rootSelector) ? [container] : container.querySelectorAll(rootSelector);
    roots.forEach(root => {
      if (!root.dataset.nativeFiltersReady && window.fetch && window.AbortController && window.DOMParser) controllers.add(new NativeBrowse(root));
    });
  }

  function syncFromRoute() {
    if (userEdited) return;
    const params = new URLSearchParams(location.search);
    document.querySelectorAll(`${rootSelector} [data-native-filter-form]`).forEach(form => {
      if (form.contains(document.activeElement)) return;
      form.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = params.getAll(input.name).includes(input.value); });
      form.querySelectorAll('input[type="number"]').forEach(input => { input.value = params.get(input.name) ?? ''; });
    });
    document.querySelectorAll(`${rootSelector} [data-native-sort]`).forEach(select => {
      const requested = params.get(select.name);
      const option = [...select.options].find(option => option.value === requested) || [...select.options].find(option => option.defaultSelected) || select.options[0];
      if (option) select.value = option.value;
    });
  }

  document.addEventListener('input', event => { if (event.target.closest(`${rootSelector} ${formSelector.split(', ').join(`, ${rootSelector} `)}`)) userEdited = true; }, true);
  document.addEventListener('change', event => { if (event.target.closest(rootSelector)) userEdited = true; }, true);
  window.addEventListener('pagehide', () => { userEdited = false; });
  const start = () => { init(); syncFromRoute(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('popstate', () => {
    controllers.forEach(instance => {
      if (instance.root.isConnected) instance.navigate(new URL(location.href), { history: false, focus: 'results' });
      else controllers.delete(instance);
    });
  });
  window.addEventListener('pageshow', () => requestAnimationFrame(syncFromRoute));
  window.addEventListener('resize', () => controllers.forEach(instance => { if (instance.root.isConnected) instance.fitPanel(); }));
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
